<template>
  <ErrorBoundary>
    <div v-if="hasMedia" class="relative my-8">
      <Heading
        :level="2"
        :title="t('product.event.customerMedia.title')"
        h-class="text-h4"
        class="kft-heading--left mb-4"
      />
      <EventGalleryGrid
        :principal-image="principalImage"
        :featured-images="featuredImages"
        :has-vertical-video="false"
        lazy-images
        @click:image="togglePreviewModalOpen"
      >
        <template v-if="isGalleryVideo(principalImage)" #main-image>
          <div
            class="relative gallery-item w-2/3 lg:w-full col-span-6 rounded-2xl overflow-hidden max-h-[420px]"
          >
            <div>
              <NuxtImg
                :src="String(image?.thumb)"
                :alt="image?.alt"
                aspect-ratio="auto"
                loading="lazy"
                class="h-full w-full object-cover"
                sizes="xs:34vw sm:34vw md:34vw lg:210px xl:210px xxl:210px"
                @click.stop.prevent="toggleLightboxModalOpen(image)"
              />
              <IconPlay
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white size-12"
                @click.stop.prevent="toggleLightboxModalOpen(image)"
              />
            </div>
          </div>
        </template>

        <template v-else #action-buttons>
          <div class="absolute top-1 right-1 flex gap-2 z-10">
            <Button
              v-if="hasPreviewPhotos"
              color="tertiary"
              class="px-2 md:px-2 py-0.5 text-sm"
              @click.stop.prevent="togglePreviewModalOpen"
            >
              {{ t('product.event.customerMedia.seeAll') }}
            </Button>
          </div>
        </template>

        <template #featured-video="{ image }">
          <div class="relative h-full w-full">
            <NuxtImg
              :src="String(image?.thumb)"
              :alt="image?.alt"
              aspect-ratio="auto"
              loading="lazy"
              class="h-full w-full object-cover"
              sizes="xs:34vw sm:34vw md:34vw lg:210px xl:210px xxl:210px"
              @click.stop.prevent="toggleLightboxModalOpen(image)"
            />
            <IconPlay
              class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white size-12"
              @click.stop.prevent="toggleLightboxModalOpen(image)"
            />
          </div>
        </template>
      </EventGalleryGrid>

      <ClientOnly>
        <TransitionRoot as="template" :show="isPreviewModalOpen">
          <Dialog class="relative z-[100]" @close="togglePreviewModalOpen">
            <div
              class="fixed inset-0 flex w-screen items-center justify-center"
            >
              <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0 translate-y-full"
                enter-to="opacity-100 translate-y-0"
                leave="duration-200 ease-in"
                leave-from="opacity-100 translate-y-0"
                leave-to="opacity-0 translate-y-full"
                as="template"
              >
                <DialogPanel class="flex flex-col w-full h-full bg-white">
                  <DialogTitle class="p-4 text-base">
                    <div>
                      <Button
                        color="tertiary"
                        class="px-2 border border-gray-300 !rounded-full"
                        @click.prevent="togglePreviewModalOpen"
                      >
                        <IconChevronDown class="rotate-90" />
                      </Button>
                    </div>
                  </DialogTitle>
                  <DialogDescription class="p-4 overflow-x-auto">
                    <EventGalleryPreview
                      :images="previewGalleryItems"
                      @click:review="handleOpenReview"
                    />
                  </DialogDescription>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </TransitionRoot>
        <TransitionRoot as="template" :show="isLightboxModalOpen">
          <Dialog class="relative z-[100]" @close="toggleLightboxModalOpen">
            <div
              class="fixed inset-0 flex w-screen items-center justify-center"
            >
              <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0 translate-y-full"
                enter-to="opacity-100 translate-y-0"
                leave="duration-200 ease-in"
                leave-from="opacity-100 translate-y-0"
                leave-to="opacity-0 translate-y-full"
                as="template"
              >
                <DialogPanel
                  class="flex flex-col align-bottom w-full h-full bg-white"
                >
                  <DialogTitle class="p-4 text-base">
                    <div>
                      <Button
                        color="tertiary"
                        class="px-2 border border-gray-300 !rounded-full"
                        @click.prevent="toggleLightboxModalOpen"
                      >
                        <IconChevronDown class="rotate-90" />
                      </Button>
                    </div>
                  </DialogTitle>
                  <DialogDescription
                    as="div"
                    class="p-4 overflow-x-auto video-dialog h-full items-center flex duration-300"
                  >
                    <LazyEventGalleryVideoPlayerSource
                      v-if="isLightboxModalOpen"
                      :source-stream="activeVideo?.streaming_url || ''"
                      :source="activeVideo?.original"
                      :poster="activeVideo?.thumb"
                      is-modal
                    />
                  </DialogDescription>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </TransitionRoot>
      </ClientOnly>
    </div>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue'
import IconChevronDown from '~/components/icons/IconChevronDown.vue'
import { Play as IconPlay } from 'lucide-vue-next'
import type { EventDescription, GalleryItem } from '~/composables/types/event'
import { toKonfettiWidthForGalleryItem } from '~/utils/mediaHelpers'

const GRID_IMAGE_WIDTH = 540
const PREVIEW_IMAGE_WIDTH = 910

const props = defineProps<{
  event: EventDescription
}>()

const emit = defineEmits<{
  (e: 'open-review', reviewId: string): void
}>()

const { t } = useI18n()
const isPreviewModalOpen = ref(false)

const togglePreviewModalOpen = () => {
  isPreviewModalOpen.value = !isPreviewModalOpen.value
}

const openReviewInSidebar = inject<((reviewId: string) => void) | undefined>(
  'openReviewInSidebar',
  undefined
)

const handleOpenReview = (reviewId?: string) => {
  if (!reviewId) return
  isPreviewModalOpen.value = false
  if (openReviewInSidebar) {
    openReviewInSidebar(reviewId)
  } else {
    emit('open-review', reviewId)
  }
}

const isGalleryVideo = (item: GalleryItem) => item.media_type === 'video'
const activeVideo = ref<GalleryItem | null>(null)

const isLightboxModalOpen = ref(false)

const toggleLightboxModalOpen = (item: GalleryItem) => {
  activeVideo.value = item
  isLightboxModalOpen.value = !isLightboxModalOpen.value
}
// Extract all media from reviews with dates and review info (customer, rating)
const allReviewMediaWithReviewInfo = computed(() => {
  return eventGetters.getAllReviewMediaWithReviewInfo(props.event)
})

type ReviewGalleryItem = GalleryItem & {
  customerName?: string
  rating?: number
  reviewId?: string
}

// Transform to GalleryItem[] with optional review overlay data (customerName, rating, reviewId)
const reviewGalleryItems = computed((): ReviewGalleryItem[] => {
  return allReviewMediaWithReviewInfo.value.map(
    ({ media, customer, rating, reviewId }) => ({
      ...eventGetters.transformMediaItemToGalleryItem(media),
      customerName: customer,
      rating,
      reviewId,
    })
  )
})

const getVideoPosterUrl = (item: GalleryItem) =>
  toKonfettiWidthForGalleryItem(
    {
      id: item.id,
      filename: item.filename,
      cropped: item.thumb || '',
      original: item.thumb || '',
      thumb: item.thumb || '',
    },
    GRID_IMAGE_WIDTH
  )

const withGridImageWidth = (item: GalleryItem): GalleryItem => {
  const resized = toKonfettiWidthForGalleryItem(item, GRID_IMAGE_WIDTH)
  return {
    ...item,
    cropped: resized,
    src: resized,
  }
}

const withGridDisplayItem = (item: GalleryItem): GalleryItem => {
  if (isGalleryVideo(item)) {
    const poster = getVideoPosterUrl(item)
    return {
      ...item,
      cropped: poster,
      src: poster,
    }
  }

  return withGridImageWidth(item)
}

const withPreviewImageWidth = <T extends GalleryItem>(item: T): T => {
  const resized = toKonfettiWidthForGalleryItem(item, PREVIEW_IMAGE_WIDTH)
  return {
    ...item,
    cropped: resized,
    src: resized,
  }
}

const previewGalleryItems = computed(() => {
  return reviewGalleryItems.value
    .filter((item) => !isGalleryVideo(item))
    .map(withPreviewImageWidth)
})

const hasPreviewPhotos = computed(() => previewGalleryItems.value.length > 0)

// Plain gallery items (for grid that doesn't need overlay)
const allGalleryItems = computed(() => {
  return reviewGalleryItems.value.map(
    ({ customerName: _c, rating: _r, reviewId: _id, ...item }) => item
  )
})

// Check if there's any media to display (need at least 4 media items)
const hasMedia = computed(() => {
  return allGalleryItems.value.length >= 4
})

// Compute principalImage (first item)
const principalImage = computed(() => {
  const firstItem = allGalleryItems.value[0]
  if (!firstItem) return {} as GalleryItem
  return withGridDisplayItem(firstItem)
})

// Compute featuredImages (remaining items, limited to 4 for preview)
const featuredImages = computed(() => {
  return allGalleryItems.value.slice(1, 5).map(withGridDisplayItem)
})
</script>
